import "./RightSideBarTaskView.scss";
import { useEditor, EditorContent } from "@tiptap/react";
import {
  useEffect,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { EmoticonReplacer } from "../../utils/emoticonExtension";
import {
  Bold01,
  Dotpoints01,
  Dotpoints02,
  Italic01,
  Hash01,
  Code01,
  MessageSquare01,
  Strikethrough01,
} from "@untitled-ui/icons-react";
import Links from "../Links/Links";
import TaskImages from "../TaskImages/TaskImages";
import ToolTip from "../ToolTip/ToolTip";
import Modal from "../Modal/Modal";
import TextColorPicker from "../../pages/Notes/Modals/TextColorPicker/TextColorPicker";
import EmojiPickerModal from "../../pages/Notes/Modals/EmojiPickerModal/EmojiPickerModal";
import { fetcher } from "../../utils/fetcher";

const RightSideBarTaskView = forwardRef(function RightSideBarTaskView(
  {
    taskData,
    hideRightSideBar = () => {},
    setEditingDescription,
    currentTask,
    fetchTasks,
    activeTab = 0,
    focusAtEnd = false,
  },
  ref,
) {
  // Use currentTask if available, otherwise fall back to taskData
  const [activeTask, setActiveTask] = useState(currentTask || taskData);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const initialContentRef = useRef("");
  const lastSavedContentRef = useRef("");

  const toolTipRef = useRef();
  const modalRef = useRef(null);

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    hasUnsavedChanges: () => hasUnsavedChanges,
    saveChanges: handleUpdate,
  }));

  const showToolTip = (e, arrow, content) => {
    const triggerElement = e.currentTarget;

    if (toolTipRef.current) {
      toolTipRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  const openModal = (e, arrow, content) => {
    const triggerElement = e.currentTarget;

    if (modalRef.current) {
      modalRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  const closeModal = () => {
    if (modalRef.current) {
      modalRef.current.close();
    }
  };

  const handleTextColorPicker = (e) => {
    openModal(
      e,
      "top-center",
      <TextColorPicker
        onSelectColor={(color) => {
          editor?.chain().focus().setColor(color).run();
          closeModal();
        }}
      />,
    );
  };

  const handleEmojiPicker = (e) => {
    openModal(
      e,
      "top-center",
      <EmojiPickerModal
        onSelectEmoji={(emoji) => {
          editor?.commands.insertContent(emoji);
          closeModal();
        }}
      />,
    );
  };

  // Update activeTask when currentTask or taskData changes
  useEffect(() => {
    setActiveTask(currentTask || taskData);
  }, [currentTask, taskData]);

  const editor = useEditor({
    extensions: [StarterKit, TextStyle, Color, EmoticonReplacer],
    content: activeTask?.description || "",
    editable: true,
    onUpdate: ({ editor }) => {
      const currentContent = editor.getHTML();
      setHasUnsavedChanges(currentContent !== initialContentRef.current);
    },
  });

  // Update editor content and track initial content when activeTask changes
  useEffect(() => {
    if (editor && activeTask?.description !== undefined) {
      const currentContent = editor.getHTML();
      const newContent = activeTask.description || "";

      // Only update if content is different to avoid cursor issues
      if (currentContent !== newContent) {
        editor.commands.setContent(newContent);
        initialContentRef.current = newContent;
        lastSavedContentRef.current = newContent;
        setHasUnsavedChanges(false);
      }
    }
  }, [editor, activeTask?.description]);

  // Set initial content reference when editor is first created
  useEffect(() => {
    if (editor) {
      initialContentRef.current = activeTask?.description || "";
      lastSavedContentRef.current = activeTask?.description || "";
    }
  }, [editor, activeTask?.description]);

  // Focus at the end of the editor content when focusAtEnd is true
  useEffect(() => {
    if (editor && focusAtEnd) {
      // Set focus and move cursor to the end
      editor.commands.focus();
      editor.commands.setTextSelection(editor.state.doc.content.size);
    }
  }, [editor, focusAtEnd]);

  // Auto-save interval
  useEffect(() => {
    if (!autoSaveEnabled || !editor || activeTab !== 0) return;

    const interval = setInterval(() => {
      const currentContent = editor.getHTML();

      // Check if there are changes from last saved
      if (currentContent !== lastSavedContentRef.current) {
        handleUpdate(false, true); // Auto-save in background without closing sidebar
      }
    }, 5000); // Auto-save every 5 seconds

    return () => clearInterval(interval);
  }, [autoSaveEnabled, editor, activeTab]);

  const handleUpdate = async (closeSidebar = true, isBackground = false) => {
    if (editor && activeTask) {
      const newDescription = editor.getHTML();

      // Don't save if nothing changed
      if (newDescription === lastSavedContentRef.current) {
        if (closeSidebar) {
          hideRightSideBar();
        }
        return;
      }

      if (isBackground) {
        // Background save: fire and forget, NO local state updates to avoid interrupting typing
        // The server has the data - that's all that matters. UI will sync when sidebar closes.
        fetcher
          .put(`/api/tasks/${activeTask._id}`, {
            description: newDescription,
          })
          .then(() => {
            // Update ONLY the refs and timestamp - don't touch activeTask or editingDescription
            lastSavedContentRef.current = newDescription;
            setLastSaved(new Date());
          })
          .catch((err) => {
            console.error("Error updating task (background):", err);
          });
        return;
      }

      // Manual save: show feedback and wait for response
      setIsSaving(true);

      try {
        // Update the task in the database
        // Only send the description field to avoid overwriting other fields with stale data
        await fetcher.put(`/api/tasks/${activeTask._id}`, {
          description: newDescription,
        });

        // Call setEditingDescription if available (for local state update)
        if (setEditingDescription) {
          setEditingDescription(newDescription);
        }

        // Update activeTask with new description
        setActiveTask((prev) => ({ ...prev, description: newDescription }));

        // Reset unsaved changes state
        initialContentRef.current = newDescription;
        lastSavedContentRef.current = newDescription;
        setHasUnsavedChanges(false);
        setLastSaved(new Date());

        // Close the right sidebar after updating only if closeSidebar is true
        if (closeSidebar) {
          hideRightSideBar();
        }
      } catch (err) {
        console.error("Error updating task:", err);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleTaskUpdate = (updatedTask) => {
    setActiveTask(updatedTask);

    // Don't call fetchTasks() here - it interrupts typing in description
    // The task list will be refreshed when sidebar closes (hideRightSideBar calls fetchTasks)
  };

  return (
    <div className="right-side-bar-task-view">
      <ToolTip ref={toolTipRef}></ToolTip>

      <div className="body-wrapper">
        {activeTab === 0 && (
          <>
            <h3 className="section-title">Description</h3>
            <div className="text-editor-wrapper">
              <div className="text-editor-buttons">
                {/* Text Formatting */}
                <div
                  className="text-editor-button"
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  onMouseEnter={(e) => showToolTip(e, "top-center", "Bold")}
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_bold
                  </span>
                </div>
                <div
                  className="text-editor-button"
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  onMouseEnter={(e) => showToolTip(e, "top-center", "Italic")}
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_italic
                  </span>
                </div>
                <div
                  className="text-editor-button"
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", "Strikethrough")
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_strikethrough
                  </span>
                </div>

                {/* Separator */}
                <div className="text-editor-separator"></div>

                {/* Headings */}
                <div
                  className="text-editor-button"
                  onClick={() =>
                    editor.chain().focus().toggleHeading({ level: 1 }).run()
                  }
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", "Heading 1")
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_h1
                  </span>
                </div>
                <div
                  className="text-editor-button"
                  onClick={() =>
                    editor.chain().focus().toggleHeading({ level: 2 }).run()
                  }
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", "Heading 2")
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_h2
                  </span>
                </div>
                <div
                  className="text-editor-button"
                  onClick={() =>
                    editor.chain().focus().toggleHeading({ level: 3 }).run()
                  }
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", "Heading 3")
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_h3
                  </span>
                </div>

                {/* Separator */}
                <div className="text-editor-separator"></div>

                {/* Text Color */}
                <div
                  className="text-editor-button"
                  onClick={handleTextColorPicker}
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", "Text Color")
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_color_text
                  </span>
                </div>

                {/* Emoji */}
                <div
                  className="text-editor-button"
                  onClick={handleEmojiPicker}
                  onMouseEnter={(e) => showToolTip(e, "top-center", "Emoji")}
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    mood
                  </span>
                </div>

                {/* Separator */}
                <div className="text-editor-separator"></div>

                {/* Lists */}
                <div
                  className="text-editor-button"
                  onClick={() =>
                    editor.chain().focus().toggleBulletList().run()
                  }
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", "Bullet List")
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_list_bulleted
                  </span>
                </div>
                <div
                  className="text-editor-button"
                  onClick={() =>
                    editor.chain().focus().toggleOrderedList().run()
                  }
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", "Numbered List")
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_list_numbered
                  </span>
                </div>

                {/* Separator */}
                <div className="text-editor-separator"></div>

                {/* Block Elements */}
                <div
                  className="text-editor-button"
                  onClick={() =>
                    editor.chain().focus().toggleBlockquote().run()
                  }
                  onMouseEnter={(e) => showToolTip(e, "top-center", "Quote")}
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_quote
                  </span>
                </div>
                <div
                  className="text-editor-button"
                  onClick={() => {
                    if (!editor) return;

                    const { from, to } = editor.state.selection;
                    const selectedText = editor.state.doc.textBetween(
                      from,
                      to,
                      "\n",
                    );

                    // If we have selected text across multiple paragraphs
                    if (selectedText && selectedText.includes("\n")) {
                      editor
                        .chain()
                        .focus()
                        .deleteSelection()
                        .insertContent({
                          type: "codeBlock",
                          content: [{ type: "text", text: selectedText }],
                        })
                        .run();
                    } else {
                      // Normal toggle for single line or empty
                      editor.chain().focus().toggleCodeBlock().run();
                    }
                  }}
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", "Code Block")
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    code
                  </span>
                </div>
              </div>
              <EditorContent editor={editor} className="tiptap-editor" />
              <div className="text-editor-bottom-bar">
                <div className="autosave-info">
                  <label className="autosave-toggle">
                    <input
                      type="checkbox"
                      checked={autoSaveEnabled}
                      onChange={(e) => setAutoSaveEnabled(e.target.checked)}
                    />
                    <span>Auto-save</span>
                  </label>
                  {isSaving && (
                    <span className="save-status saving">Saving...</span>
                  )}
                  {!isSaving && lastSaved && (
                    <span className="save-status saved">
                      Saved {new Date(lastSaved).toLocaleTimeString()}
                    </span>
                  )}
                </div>
                <div
                  className="text-editor-update-button"
                  onClick={() => handleUpdate(true)}
                >
                  Update & Close
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 1 && (
          <Links taskData={activeTask} onTaskUpdate={handleTaskUpdate} />
        )}

        {activeTab === 2 && (
          <TaskImages taskData={activeTask} onTaskUpdate={handleTaskUpdate} />
        )}
      </div>

      {/* <ul>
        <li>Status</li>
        <li>Person</li>
        <li>Priority</li>
        <li>Description</li>
      </ul>

      <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {JSON.stringify(taskData, null, 2)}
      </pre> */}

      <Modal ref={modalRef} />
    </div>
  );
});

export default RightSideBarTaskView;
